import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Card,
  CardContent,
  Tooltip,
  Badge,
  useTheme,
  alpha
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Event,
  Warning,
  CheckCircle,
  Schedule,
  Add
} from '@mui/icons-material';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, isToday, addMonths, subMonths } from 'date-fns';
import { ro } from 'date-fns/locale';
import { useNotifications } from '@contexts/NotificationContext';

// Importam interfata Appointment din appointmentsService pentru compatibilitate
import { Appointment } from '@services/appointmentsService';

interface CalendarMiniViewProps {
  appointments: Appointment[];
  onDateClick?: (date: Date) => void;
  onAddAppointment?: () => void;
}

const CalendarMiniView: React.FC<CalendarMiniViewProps> = ({
  appointments,
  onDateClick,
  onAddAppointment
}) => {
  const theme = useTheme();
  const { addNotification } = useNotifications();
  const [currentDate, setCurrentDate] = useState(new Date());

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Adaug zilele din saptamana anterioara pentru a completa prima saptamana
  const firstDayOfWeek = monthStart.getDay();
  const daysFromPrevMonth = Array.from({ length: firstDayOfWeek }, (_, i) => {
    const prevMonth = subMonths(currentDate, 1);
    const prevMonthEnd = endOfMonth(prevMonth);
    return new Date(prevMonthEnd.getTime() - (firstDayOfWeek - i - 1) * 24 * 60 * 60 * 1000);
  });

  const allDays = [...daysFromPrevMonth, ...daysInMonth];

  const getAppointmentsForDate = (date: Date) => {
    return appointments.filter(appointment => {
      // Verifica daca programarea are o data care se potriveste cu ziua selectata
      if (appointment.date) {
        const appointmentDate = new Date(appointment.date);
        return isSameDay(appointmentDate, date);
      }
      // Verifica si scheduledAt sau appointmentDate (sunt string-uri)
      if (appointment.scheduledAt) {
        const appointmentDate = new Date(appointment.scheduledAt);
        return isSameDay(appointmentDate, date);
      }
      if (appointment.appointmentDate) {
        const appointmentDate = new Date(appointment.appointmentDate);
        return isSameDay(appointmentDate, date);
      }
      return false;
    });
  };

  const _getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'high':
        return theme.palette.error.main;
      case 'medium':
        return theme.palette.warning.main;
      case 'low':
        return theme.palette.success.main;
      default:
        return theme.palette.grey[500];
    }
  };

  const _getStatusIcon = (status: string) => {
    switch (status) {
      case 'confirmed':
        return <CheckCircle sx={{ fontSize: 12, color: theme.palette.success.main }} />;
      case 'scheduled':
        return <Schedule sx={{ fontSize: 12, color: theme.palette.info.main }} />;
      case 'completed':
        return <CheckCircle sx={{ fontSize: 12, color: theme.palette.success.main }} />;
      default:
        return <Event sx={{ fontSize: 12, color: theme.palette.grey[500] }} />;
    }
  };

  const handlePrevMonth = () => {
    setCurrentDate(subMonths(currentDate, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(addMonths(currentDate, 1));
  };

  const handleDateClick = (date: Date) => {
    const dayAppointments = getAppointmentsForDate(date);
    
    // Simuleaza notificare pentru click pe data
    if (dayAppointments.length > 0) {
      addNotification({
        type: 'appointment_reminder',
        title: `Programari pentru ${format(date, 'dd MMMM', { locale: ro })}`,
        message: `${dayAppointments.length} programari in aceasta zi`,
        userId: 'current_user',
        priority: 'low'
      });
    }
    
    if (onDateClick) {
      onDateClick(date);
    }
  };

  const handleAddAppointment = () => {
    // Simuleaza notificare pentru adaugare programare
    addNotification({
      type: 'appointment_created',
      title: 'Programare noua',
      message: 'Programarea a fost creata cu succes si va fi confirmata de medic',
      userId: 'current_user',
      priority: 'medium'
    });
    
    if (onAddAppointment) {
      onAddAppointment();
    }
  };

  return (
    <Card sx={{ 
      borderRadius: 3, 
      transition: 'all 0.3s ease',
      '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }
    }}>
      <CardContent sx={{ p: 3 }}>
        {/* Header cu navigare si buton adaugare */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton 
              onClick={handlePrevMonth}
              size="small"
              sx={{ 
                color: 'text.secondary',
                '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.08) }
              }}
            >
              <ChevronLeft />
            </IconButton>
            
            <Typography variant="h6" sx={{ 
              fontWeight: 600, 
              minWidth: 120,
              textAlign: 'center'
            }}>
              {format(currentDate, 'MMMM yyyy', { locale: ro })}
            </Typography>
            
            <IconButton 
              onClick={handleNextMonth}
              size="small"
              sx={{ 
                color: 'text.secondary',
                '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.08) }
              }}
            >
              <ChevronRight />
            </IconButton>
          </Box>

          <Tooltip title="Adauga programare">
            <IconButton
              onClick={handleAddAppointment}
              sx={{
                backgroundColor: theme.palette.primary.main,
                color: 'white',
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                  transform: 'scale(1.05)'
                },
                transition: 'all 0.2s ease'
              }}
            >
              <Add />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Zilele saptamanii */}
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(7, 1fr)', 
          gap: 1, 
          mb: 1 
        }}>
          {['L', 'Ma', 'Mi', 'J', 'V', 'S', 'D'].map((day) => (
            <Box key={day}>
              <Typography 
                variant="caption" 
                sx={{ 
                  fontWeight: 600, 
                  color: 'text.secondary',
                  textAlign: 'center',
                  display: 'block'
                }}
              >
                {day}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Calendar grid */}
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(7, 1fr)', 
          gap: 1 
        }}>
          {allDays.map((day, index) => {
            const dayAppointments = getAppointmentsForDate(day);
            const isCurrentMonth = isSameMonth(day, currentDate);
            const isCurrentDay = isToday(day);
            const hasAppointments = dayAppointments.length > 0;
            const urgentAppointments = dayAppointments.filter(app => app.urgency === 'high');

            return (
              <Box key={index}>
                <Tooltip 
                  title={
                    hasAppointments ? (
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                          Programari {format(day, 'dd MMMM yyyy', { locale: ro })}
                        </Typography>
                        {dayAppointments.map((appointment, idx) => (
                          <Box key={idx} sx={{ mb: 0.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {appointment.time || 'N/A'} - {appointment.animalName || appointment.animal || 'N/A'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              {appointment.type || 'N/A'} ({appointment.urgency || 'N/A'})
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      `Nu sunt programari pentru ${format(day, 'dd MMMM yyyy', { locale: ro })}`
                    )
                  }
                  arrow
                  placement="top"
                >
                  <Box
                    onClick={() => handleDateClick(day)}
                    sx={{
                      aspectRatio: '1',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: 2,
                      cursor: 'pointer',
                      position: 'relative',
                      backgroundColor: isCurrentDay 
                        ? alpha(theme.palette.primary.main, 0.1)
                        : hasAppointments 
                          ? alpha(theme.palette.warning.main, 0.05)
                          : 'transparent',
                      border: isCurrentDay 
                        ? `2px solid ${theme.palette.primary.main}`
                        : hasAppointments 
                          ? `1px solid ${alpha(theme.palette.warning.main, 0.3)}`
                          : '1px solid transparent',
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.05),
                        transform: 'scale(1.05)',
                        zIndex: 1
                      },
                      transition: 'all 0.2s ease',
                      opacity: isCurrentMonth ? 1 : 0.4
                    }}
                  >
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      fontWeight: isCurrentDay ? 700 : 500,
                      color: isCurrentDay ? 'primary.main' : 'text.primary',
                      fontSize: '0.75rem'
                    }}
                  >
                    {format(day, 'd')}
                  </Typography>

                  {/* Puncte colorate pentru tipul programarilor */}
                  {hasAppointments && (
                    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                      {dayAppointments.slice(0, 3).map((appointment, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            width: 4,
                            height: 4,
                            borderRadius: '50%',
                            backgroundColor: appointment.urgency === 'high' 
                              ? theme.palette.error.main 
                              : appointment.urgency === 'medium'
                                ? theme.palette.warning.main
                                : theme.palette.success.main,
                            opacity: appointment.status === 'confirmed' ? 1 : 0.6
                          }}
                        />
                      ))}
                    </Box>
                  )}

                  {/* Badge pentru programari */}
                  {hasAppointments && (
                    <Badge
                      badgeContent={dayAppointments.length}
                      max={3}
                      sx={{
                        '& .MuiBadge-badge': {
                          backgroundColor: urgentAppointments.length > 0 
                            ? theme.palette.error.main 
                            : theme.palette.primary.main,
                          fontSize: '0.6rem',
                          minWidth: 16,
                          height: 16
                        }
                      }}
                    >
                      <Box sx={{ width: 8, height: 8 }} />
                    </Badge>
                  )}

                  {/* Indicator urgenta */}
                  {urgentAppointments.length > 0 && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 2,
                        right: 2,
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        backgroundColor: theme.palette.error.main,
                        animation: 'pulse 2s infinite'
                      }}
                    />
                  )}
                </Box>
              </Tooltip>
            </Box>
          );
        })}
        </Box>

        {/* Legenda */}
        <Box sx={{ mt: 3, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Chip
            icon={<Event />}
            label="Programari"
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.7rem' }}
          />
          <Chip
            icon={<Warning />}
            label="Urgenta"
            size="small"
            variant="outlined"
            color="error"
            sx={{ fontSize: '0.7rem' }}
          />
          <Chip
            icon={<CheckCircle />}
            label="Confirmate"
            size="small"
            variant="outlined"
            color="success"
            sx={{ fontSize: '0.7rem' }}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

export default CalendarMiniView; 
